import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import type { LoginRequest, RegisterRequest, UserProfile, UserRole } from "@/shared/api/types"
import { isApiError } from "@/shared/api/client"
import {
  clearTokens,
  getAccessToken,
  subscribeToTokens,
} from "@/shared/auth/tokenStore"
import { loginAccount, logoutAccount, registerAccount, restoreAccount } from "./authService"
import { loginPath } from "./returnTo"
import { AuthContext, type AuthContextValue } from "./context"

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [account, setAccount] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let active = true
    void restoreAccount()
      .then((current) => {
        if (active) setAccount(current)
      })
      .catch((cause) => {
        if (active) {
          clearTokens()
          // 401/403 on cookie refresh: treat as logged-out, not a fatal auth error.
          if (!isApiError(cause) || (cause.status !== 401 && cause.status !== 403)) {
            setError(cause instanceof Error ? cause : new Error("Session restore failed"))
          }
        }
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    const unsubscribe = subscribeToTokens(() => {
      if (!getAccessToken()) {
        setAccount(null)
        setIsLoading(false)
        queryClient.clear()
      }
    })
    return () => {
      active = false
      unsubscribe()
    }
  }, [queryClient])

  const login = useCallback(
    (returnTo?: string) => {
      const current = `${location.pathname}${location.search}${location.hash}`
      navigate(loginPath(returnTo ?? current))
    },
    [location.hash, location.pathname, location.search, navigate],
  )

  const authenticate = useCallback(async (credentials: LoginRequest) => {
    setError(null)
    const current = await loginAccount(credentials)
    setAccount(current)
    return current
  }, [])

  const register = useCallback(async (details: RegisterRequest) => {
    setError(null)
    const current = await registerAccount(details)
    setAccount(current)
    return current
  }, [])

  const logout = useCallback(async () => {
    try {
      await logoutAccount()
    } finally {
      clearTokens()
      setAccount(null)
      queryClient.clear()
      navigate("/", { replace: true })
    }
  }, [navigate, queryClient])

  const hasRole = useCallback(
    (role: UserRole | UserRole[]) => {
      const wanted = Array.isArray(role) ? role : [role]
      return wanted.some((item) => account?.roles.includes(item))
    },
    [account?.roles],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoading,
      isAuthenticated: Boolean(account),
      account,
      error,
      login,
      authenticate,
      register,
      logout,
      hasRole,
    }),
    [account, authenticate, error, hasRole, isLoading, login, logout, register],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
